/**
 * Vendor-related Supabase queries
 * Pure async functions - no React, no useQuery
 */

import { supabase } from "@/integrations/supabase/client";

// Types
export interface Vendor {
  id: string;
  name: string;
  category: string;
  email?: string;
  phone?: string;
  is_active: boolean;
}

/**
 * Get all active vendors for a company
 */
export async function getVendors(companyId: string): Promise<Vendor[]> {
  if (!companyId) return [];

  const { data, error } = await supabase
    .from("vendors")
    .select("id, name, category, email, phone, is_active")
    .eq("company_id", companyId)
    .eq("is_active", true)
    .order("name");

  if (error) throw error;
  return (data || []) as Vendor[];
}

/**
 * Per-vendor order stats (total orders, total spent) for display in vendor list
 */
export interface VendorOrderStat {
  vendor_id: string;
  total_orders: number;
  total_spent: number;
}

export async function getVendorOrderStats(companyId: string): Promise<VendorOrderStat[]> {
  if (!companyId) return [];

  const { data: orders, error } = await supabase
    .from("vendor_orders")
    .select("vendor_id, total_amount")
    .eq("company_id", companyId);

  if (error) throw error;

  const map = new Map<string, { total_orders: number; total_spent: number }>();
  (orders || []).forEach((o: { vendor_id: string; total_amount: string | number }) => {
    const id = o.vendor_id;
    if (!map.has(id)) map.set(id, { total_orders: 0, total_spent: 0 });
    const s = map.get(id)!;
    s.total_orders += 1;
    s.total_spent += Number(o.total_amount);
  });

  return Array.from(map.entries()).map(([vendor_id, v]) => ({
    vendor_id,
    total_orders: v.total_orders,
    total_spent: v.total_spent,
  }));
}

/**
 * Get a single vendor by ID
 */
export async function getVendorById(companyId: string, vendorId: string): Promise<Vendor | null> {
  if (!companyId || !vendorId) return null;

  const { data, error } = await supabase
    .from("vendors")
    .select("id, name, category, email, phone, is_active")
    .eq("company_id", companyId)
    .eq("id", vendorId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as Vendor;
}
